import {
  Button,
  Card,
  DataStatus,
  Form,
  FormField,
  openDialog,
  Tag,
  useLocalStorage,
} from 'platform/components';
import {Box, HStack, Text, VStack} from 'platform/foundation';

import {useEffect, useRef, useState, RefObject} from 'react';

import {isNil} from 'ramda';
import {isNotNil} from 'ramda-adjunct';

import {
  GeneratedPlatformDescription,
  GenerateDescriptionRequestBody,
  useGenerateDescriptionMutation,
  useGeneratePlatformDescriptionsMutation,
  useGetActiveQuickPresetsQuery,
} from '@dms/api/sales';
import {useGetDescriptionGeneratorConfigQuery} from '@dms/api/settings';
import i18n from '@dms/i18n';
import {settingsRoutes} from '@dms/routes';
import {handleApiError, useTenant} from '@dms/shared';

import {ApiError, TestIdProps, getSafely, useNavigate} from 'shared/web';

import {
  type DescriptionGeneratorIncludeKey,
  DescriptionGeneratorInputs,
} from '../types/DescriptionGeneratorInputs';
import {buildGenerateBody} from '../utils/buildGenerateBody';
import {localeToLanguage} from '../utils/localeToLanguage';
import {isSupportedLanguage, toLanguage} from '../utils/toLanguage';
import {AddQuickPresetDialog} from './AddQuickPresetDialog';

type DescriptionGeneratorForm = DescriptionGeneratorInputs;

const INCLUDE_OPTIONS = [
  {
    label: i18n.t('entity.advertisingPlatforms.labels.includeModelShort'),
    value: 'includeModel',
  },
  {
    label: i18n.t('entity.advertisingPlatforms.labels.includeEngineShort'),
    value: 'includeEngine',
  },
  {
    label: i18n.t('entity.advertisingPlatforms.labels.includeFeaturesShort'),
    value: 'includeFeatures',
  },
  {
    label: i18n.t('entity.advertisingPlatforms.labels.includeConditionShort'),
    value: 'includeCondition',
  },
  {
    label: i18n.t('entity.advertisingPlatforms.labels.includeBranchShort'),
    value: 'includeBranch',
  },
];

const DEFAULT_INCLUDE_KEYS = [
  'includeModel',
  'includeEngine',
  'includeFeatures',
  'includeCondition',
  'includeBranch',
] as const satisfies readonly DescriptionGeneratorIncludeKey[];

export interface DescriptionGeneratorProps extends TestIdProps {
  vehicleId: string;
  onGenerate?: (content: string) => void;
  onPlatformsGenerated?: (platforms: GeneratedPlatformDescription[]) => void;
  enabledLanguages?: Array<{value: string; label: string}>;
  onGenerateLocalized?: (results: Array<{language: string; content: string}>) => void;
  inputsRef?: RefObject<DescriptionGeneratorInputs | null>;
}

export function DescriptionGenerator(props: DescriptionGeneratorProps) {
  const localStorageKey = `descriptionGenerator_isExpanded_${props.vehicleId}`;
  const [isExpanded, setIsExpanded] = useLocalStorage(localStorageKey, true);
  const [generateDescription, {isLoading: isGenerating}] = useGenerateDescriptionMutation();
  const [generatePlatformDescriptions, {isLoading: isPlatformGenerating}] =
    useGeneratePlatformDescriptionsMutation();
  const {data: descriptionGeneratorConfig, isLoading: isConfigLoading} =
    useGetDescriptionGeneratorConfigQuery({});
  const {data: activeQuickPresets, isLoading: isQuickPresetsLoading} =
    useGetActiveQuickPresetsQuery({vehicleId: props.vehicleId});
  const {tenant} = useTenant();

  const navigate = useNavigate();

  const [isBatchLoading, setIsBatchLoading] = useState(false);
  const isMountedRef = useRef(true);
  const inFlightRef = useRef<Array<{abort: () => void}>>([]);

  const isGeneratingAny = isGenerating || isBatchLoading || isPlatformGenerating;

  useEffect(function abortInFlightRequestsOnUnmount() {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
      inFlightRef.current.forEach((p) => p.abort());
      inFlightRef.current = [];
    };
  }, []);

  const requestPlatformDescriptions = async (platformBody: GenerateDescriptionRequestBody) => {
    const platformRequest = generatePlatformDescriptions({
      vehicleId: props.vehicleId,
      body: platformBody,
    });
    inFlightRef.current = [...inFlightRef.current, platformRequest];

    try {
      const response = await platformRequest.unwrap();
      return response.platforms;
    } catch (error) {
      if (isMountedRef.current) {
        handleApiError(error as ApiError);
      }
      return undefined;
    }
  };

  // eslint-disable-next-line max-statements -- Pre-existing violation
  const handleGenerate = async (data: DescriptionGeneratorForm) => {
    if (isNil(descriptionGeneratorConfig) || descriptionGeneratorConfig.enabled !== true) {
      return;
    }

    const body = buildGenerateBody(descriptionGeneratorConfig, data);

    const enabledLanguages = props.enabledLanguages;
    if (isNotNil(enabledLanguages) && enabledLanguages.length > 0) {
      const supported = enabledLanguages.filter((lang) => isSupportedLanguage(lang.value));

      if (supported.length === 0) {
        return;
      }

      setIsBatchLoading(true);
      const requests = supported.map((lang) => ({
        lang,
        request: generateDescription({
          vehicleId: props.vehicleId,
          body: {...body, language: toLanguage(lang.value)},
        }),
      }));
      inFlightRef.current = requests.map(({request}) => request);

      try {
        const results = await Promise.allSettled(
          requests.map(({lang, request}) =>
            request.unwrap().then((response) => ({language: lang.value, content: response.content}))
          )
        );

        if (!isMountedRef.current) {
          return;
        }

        const successful: Array<{language: string; content: string}> = [];
        const failures: PromiseRejectedResult[] = [];
        results.forEach((result) => {
          if (result.status === 'fulfilled') {
            successful.push(result.value);
          } else {
            failures.push(result);
          }
        });

        failures.forEach((failure) => handleApiError(failure.reason));

        if (successful.length > 0) {
          const platformLanguage =
            localeToLanguage(tenant?.defaultLocale) ?? descriptionGeneratorConfig.language;
          const platforms = await requestPlatformDescriptions({
            ...body,
            language: platformLanguage,
          });
          // eslint-disable-next-line max-depth -- Pre-existing violation
          if (!isMountedRef.current) {
            return;
          }
          props.onGenerateLocalized?.(successful);
          // eslint-disable-next-line max-depth -- Pre-existing violation
          if (platforms) {
            props.onPlatformsGenerated?.(platforms);
          }
        }
      } finally {
        inFlightRef.current = [];
        if (isMountedRef.current) {
          setIsBatchLoading(false);
        }
      }
      return;
    }

    const request = generateDescription({
      vehicleId: props.vehicleId,
      body: {...body, language: descriptionGeneratorConfig.language},
    });
    inFlightRef.current = [request];

    try {
      const response = await request.unwrap();
      if (!isMountedRef.current) {
        return;
      }
      const platforms = await requestPlatformDescriptions({
        ...body,
        language: descriptionGeneratorConfig.language,
      });
      if (!isMountedRef.current) {
        return;
      }
      props.onGenerate?.(response.content);
      if (platforms) {
        props.onPlatformsGenerated?.(platforms);
      }
    } catch (error) {
      if (isMountedRef.current) {
        handleApiError(error as ApiError);
      }
    } finally {
      inFlightRef.current = [];
    }
  };

  return (
    <Card
      title={i18n.t('entity.advertisingPlatforms.labels.enableDescriptionGenerator')}
      isExpandable={false} // Hide chevron, we use switch instead
      isExpanded={descriptionGeneratorConfig?.enabled && isExpanded}
      hasSeparator={false}
      variant="inlineGrey"
      control={{
        type: 'switch',
        value: !!descriptionGeneratorConfig?.enabled && isExpanded,
        isDisabled: !descriptionGeneratorConfig?.enabled,
        onChange: setIsExpanded,
      }}
      actions={[
        {
          type: 'button',
          title: i18n.t('general.labels.settings'),
          variant: 'link',
          leftIcon: 'action/settings',
          onClick: () => navigate(settingsRoutes.advertisingPlatforms),
        },
      ]}
    >
      <DataStatus isLoading={isConfigLoading || isQuickPresetsLoading}>
        <Form<DescriptionGeneratorForm>
          defaultValues={{
            includes: DEFAULT_INCLUDE_KEYS.filter((key) =>
              Boolean(getSafely(descriptionGeneratorConfig, key))
            ),
            additionalInstruction: descriptionGeneratorConfig?.additionalInstruction ?? null,
            quickPresets: activeQuickPresets ?? [],
          }}
          onSubmit={handleGenerate}
        >
          {(control, formApi) => {
            // Watch all three fields so the inputsRef below stays in sync
            // when includes/additionalInstruction change (not only quickPresets).
            const [quickPresets] = formApi.watch([
              'quickPresets',
              'includes',
              'additionalInstruction',
            ]);

            if (props.inputsRef) {
              props.inputsRef.current = formApi.getValues();
            }

            return (
              <VStack spacing={4}>
                <Text size="xSmall" color="tertiary">
                  {i18n.t('entity.advertisingPlatforms.labels.descriptionGeneratorDescription')}
                </Text>
                <FormField
                  name="includes"
                  control={control}
                  type="chips"
                  isMultiple
                  size="small"
                  options={INCLUDE_OPTIONS}
                  label={i18n.t('entity.advertisingPlatforms.labels.descriptionIncludes')}
                />
                <VStack spacing={1}>
                  <Text size="xSmall" color="secondary">
                    {i18n.t('entity.advertisingPlatforms.labels.descriptionQuickPresetDescription')}
                  </Text>
                  <HStack spacing={1} align="center" wrap>
                    {quickPresets.map((preset) => (
                      <Tag
                        key={preset.id}
                        onDelete={() => {
                          if (!isGeneratingAny) {
                            formApi.setValue(
                              'quickPresets',
                              quickPresets.filter((qp) => qp.id !== preset.id)
                            );
                          }
                        }}
                        label={preset.name}
                        colorScheme="purple"
                        data-testid={`quick-preset-tag-${preset.id}`}
                      />
                    ))}
                    <Button
                      variant="link"
                      leftIcon="content/add_circle"
                      isDisabled={isGeneratingAny}
                      onClick={() =>
                        openDialog(
                          <AddQuickPresetDialog
                            selectedPresets={quickPresets}
                            onSelect={(value) =>
                              formApi.setValue('quickPresets', [
                                ...quickPresets.filter((qp) => qp.id !== value.id),
                                value,
                              ])
                            }
                          />,
                          {
                            size: 'small',
                            title: i18n.t(
                              'entity.advertisingPlatforms.labels.descriptionAddQuickPreset'
                            ),
                          }
                        )
                      }
                      title={i18n.t('entity.advertisingPlatforms.labels.descriptionAddQuickPreset')}
                    />
                  </HStack>
                </VStack>
                <HStack align="flex-end" justify="space-between" grow={3} spacing={4}>
                  <Box flexGrow={1}>
                    <FormField
                      name="additionalInstruction"
                      control={control}
                      type="textarea"
                      isResizable
                      label={i18n.t('entity.advertisingPlatforms.labels.additionalInstruction')}
                      tooltip={i18n.t(
                        'entity.advertisingPlatforms.labels.additionalInstructionTooltip'
                      )}
                    />
                  </Box>
                  <Button
                    variant="primary"
                    leftIcon={isGeneratingAny ? 'action/ai_robot' : 'action/ai'}
                    type="submit"
                    isDisabled={isGeneratingAny}
                    title={
                      isGeneratingAny
                        ? i18n.t('entity.advertisingPlatforms.labels.generating')
                        : i18n.t('entity.advertisingPlatforms.labels.generate')
                    }
                  />
                </HStack>
              </VStack>
            );
          }}
        </Form>
      </DataStatus>
    </Card>
  );
}
